import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { sensorService } from '../services/sensorService';
import { SensorReading } from '../types';
import { getStoredTempUnit, convertTemperature, tempUnitLabel } from '../utils/temperatureUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface LHT65NReadingsChartProps {
  sensorId: number;
  latestReading?: SensorReading | null;
  onNewReading?: (reading: SensorReading) => void;
}

type DateRangePreset = '1d' | '7d' | '30d' | '90d' | 'custom';

const LHT65NReadingsChart: React.FC<LHT65NReadingsChartProps> = ({
  sensorId,
  latestReading,
  onNewReading,
}) => {
  const { t } = useTranslation();
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showTemperature, setShowTemperature] = useState(true);
  const [showHumidity, setShowHumidity] = useState(true);

  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('1d');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastReadingIdRef = useRef<number | null>(null);
  const isPollingInProgressRef = useRef<boolean>(false);

  const tempUnit = getStoredTempUnit();

  const getDateRange = useCallback(() => {
    const end = new Date();
    let start = new Date();

    if (dateRangePreset === 'custom' && startDate && endDate) {
      return { start: new Date(startDate), end: new Date(endDate) };
    }

    switch (dateRangePreset) {
      case '1d': start.setDate(start.getDate() - 1); break;
      case '7d': start.setDate(start.getDate() - 7); break;
      case '30d': start.setDate(start.getDate() - 30); break;
      case '90d': start.setDate(start.getDate() - 90); break;
    }

    return { start, end };
  }, [dateRangePreset, startDate, endDate]);

  const getTimeUnit = useCallback(() => {
    const { start, end } = getDateRange();
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= 1) return 'hour';
    if (diffDays <= 60) return 'day';
    return 'week';
  }, [getDateRange]);

  const loadReadings = useCallback(async (isPolling = false) => {
    if (isPolling && isPollingInProgressRef.current) return;
    if (isPolling) {
      isPollingInProgressRef.current = true;
    } else {
      setLoading(true);
    }

    try {
      const { start, end } = getDateRange();
      const data = await sensorService.getReadings(sensorId, {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        limit: 5000,
      });

      const sortedData = data.sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      setReadings(sortedData);

      if (isPolling && sortedData.length > 0) {
        const newestReading = sortedData[sortedData.length - 1];
        if (lastReadingIdRef.current !== null && newestReading.id !== lastReadingIdRef.current) {
          if (onNewReading) onNewReading(newestReading);
        }
        lastReadingIdRef.current = newestReading.id;
      } else if (!isPolling && sortedData.length > 0) {
        lastReadingIdRef.current = sortedData[sortedData.length - 1].id;
      }

      setError(null);
    } catch (err: any) {
      if (!isPolling) setError(err.response?.data?.detail || t('chart.failedToLoad'));
    } finally {
      if (isPolling) {
        isPollingInProgressRef.current = false;
      } else {
        setLoading(false);
      }
    }
  }, [sensorId, getDateRange, onNewReading, t]);

  useEffect(() => { loadReadings(); }, [loadReadings]);

  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => loadReadings(true), 30000);
    return () => { if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current); };
  }, [loadReadings]);

  useEffect(() => {
    if (latestReading) {
      setReadings(prev => {
        const exists = prev.some(r => r.id === latestReading.id);
        if (!exists) {
          return [...prev, latestReading].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        }
        return prev;
      });
    }
  }, [latestReading]);

  const handlePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    if (preset !== 'custom') { setStartDate(''); setEndDate(''); }
  };

  const handleCustomDateChange = () => {
    if (startDate && endDate) setDateRangePreset('custom');
  };

  const latestTemp = latestReading?.air_temperature != null
    ? convertTemperature(latestReading.air_temperature, tempUnit)
    : null;
  const latestHumidity = latestReading?.air_humidity ?? null;

  const chartData = {
    labels: readings.map(r => new Date(r.timestamp)),
    datasets: [
      ...(showTemperature ? [{
        label: `${t('lht65n.airTemperature')} (${tempUnitLabel(tempUnit)})`,
        data: readings.map(r =>
          r.air_temperature != null ? convertTemperature(r.air_temperature, tempUnit) : null
        ),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y',
      }] : []),
      ...(showHumidity ? [{
        label: `${t('lht65n.humidity')} (%)`,
        data: readings.map(r => r.air_humidity ?? null),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y1',
      }] : []),
    ],
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: t('lht65n.chartTitle') },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (items.length > 0 && items[0].parsed.x != null) {
              return new Date(items[0].parsed.x).toLocaleString();
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: getTimeUnit(),
          displayFormats: { hour: 'HH:mm', day: 'MMM d', week: 'MMM d' },
        },
        title: { display: true, text: 'Time' },
      },
      y: {
        type: 'linear',
        display: showTemperature,
        position: 'left',
        title: {
          display: true,
          text: `${t('lht65n.airTemperature')} (${tempUnitLabel(tempUnit)})`,
          color: 'rgb(239, 68, 68)',
        },
        ticks: { color: 'rgb(239, 68, 68)' },
      },
      y1: {
        type: 'linear',
        display: showHumidity,
        position: 'right',
        min: 0,
        max: 100,
        title: {
          display: true,
          text: `${t('lht65n.humidity')} (%)`,
          color: 'rgb(59, 130, 246)',
        },
        ticks: { color: 'rgb(59, 130, 246)' },
        grid: { drawOnChartArea: false },
      },
    },
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {/* Latest reading summary */}
      {(latestTemp !== null || latestHumidity !== null) && (
        <div className="mb-4 flex flex-wrap items-center gap-6">
          {latestTemp !== null && (
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-red-600 text-lg">🌡️</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {latestTemp.toFixed(1)} {tempUnitLabel(tempUnit)}
                </p>
                <p className="text-xs text-gray-500">{t('lht65n.airTemperature')}</p>
              </div>
            </div>
          )}
          {latestHumidity !== null && (
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 text-lg">💧</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {latestHumidity.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500">{t('lht65n.humidity')}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowTemperature(!showTemperature)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                showTemperature
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-white text-red-500 border-red-500 hover:bg-red-50'
              }`}
            >
              {t('lht65n.airTemperature')}
            </button>
            <button
              onClick={() => setShowHumidity(!showHumidity)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                showHumidity
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
              }`}
            >
              {t('lht65n.humidity')}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {(['1d', '7d', '30d', '90d'] as DateRangePreset[]).map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetChange(preset)}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  dateRangePreset === preset
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {preset === '1d' ? t('chart.hours24') : preset === '7d' ? t('chart.days7') : preset === '30d' ? t('chart.days30') : t('chart.days90')}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('chart.startDate')}</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('chart.endDate')}</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleCustomDateChange}
            disabled={!startDate || !endDate}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('chart.applyCustomRange')}
          </button>
          <button
            onClick={() => loadReadings()}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            {t('chart.refresh')}
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="relative" style={{ height: '400px' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <div className="text-gray-600">{t('chart.loadingReadings')}</div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-red-600">{error}</div>
          </div>
        ) : readings.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gray-500">{t('chart.noReadingsForRange')}</div>
          </div>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>

      {!loading && readings.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-right">
          {t('chart.showingReading', { count: readings.length })}
          {dateRangePreset !== 'custom' && ` ${dateRangePreset === '1d' ? t('chart.fromLast24h') : dateRangePreset === '7d' ? t('chart.fromLast7d') : dateRangePreset === '30d' ? t('chart.fromLast30d') : t('chart.fromLast90d')}`}
        </div>
      )}
    </div>
  );
};

export default LHT65NReadingsChart;
